import { useEffect, useState } from "react";
import "./App.scss";
import TextField from "@mui/material/TextField";

function App() {
  function valuetext(value: number) {
    return `${value}°C`;
  }

  const amountOfDelves = 4;
  const amountOfKills = 1;
  const [wave, setWave] = useState<number>(amountOfDelves);
  const [kills, setKills] = useState<number>(amountOfKills);

  return (
    <div className="App">
      <div className="bg-blur"></div>
      <header className="App-header">
        <h1>Doom of mokhaiotl - Delve boss</h1>
      </header>
      <main>
        <div className="inputs">
          <TextField
            id="outlined-basic"
            label="Amount of delves (Wave)"
            variant="filled"
            color="error"
            type="number"
            onChange={(e) => setWave(parseInt(e.target.value))}
          />
          <TextField
            id="outlined-basic"
            label="Amount of completions"
            variant="filled"
            color="error"
            type="number"
            onChange={(e) => setKills(parseInt(e.target.value))}
          />
        </div>
        <div className="loot-calculations">
          <h2>Result</h2>
          <WaveDropCalculator currentWave={wave} kills={kills} />
        </div>
      </main>
    </div>
  );
}

export default App;

type DropEntry = { wave: number; dropRate: number };

const mokhaiotlCloth: DropEntry[] = [
  { wave: 1, dropRate: 0 },
  { wave: 2, dropRate: 1 / 2500 },
  { wave: 3, dropRate: 1 / 2000 },
  { wave: 4, dropRate: 1 / 1350 },
  { wave: 5, dropRate: 1 / 810 },
  { wave: 6, dropRate: 1 / 765 },
  { wave: 7, dropRate: 1 / 720 },
  { wave: 8, dropRate: 1 / 630 },
  { wave: 9, dropRate: 1 / 540 },
];

const eyeOfAyak: DropEntry[] = [
  { wave: 1, dropRate: 0 },
  { wave: 2, dropRate: 0 },
  { wave: 3, dropRate: 1 / 2000 },
  { wave: 4, dropRate: 1 / 1350 },
  { wave: 5, dropRate: 1 / 810 },
  { wave: 6, dropRate: 1 / 765 },
  { wave: 7, dropRate: 1 / 720 },
  { wave: 8, dropRate: 1 / 630 },
  { wave: 9, dropRate: 1 / 540 },
];

const avernicThreads: DropEntry[] = [
  { wave: 1, dropRate: 0 },
  { wave: 2, dropRate: 0 },
  { wave: 3, dropRate: 0 },
  { wave: 4, dropRate: 1 / 1350 },
  { wave: 5, dropRate: 1 / 810 },
  { wave: 6, dropRate: 1 / 765 },
  { wave: 7, dropRate: 1 / 720 },
  { wave: 8, dropRate: 1 / 630 },
  { wave: 9, dropRate: 1 / 540 },
];

const petRates: DropEntry[] = [
  { wave: 1, dropRate: 0 },
  { wave: 2, dropRate: 0 },
  { wave: 3, dropRate: 0 },
  { wave: 4, dropRate: 0 },
  { wave: 5, dropRate: 0 },
  { wave: 6, dropRate: 1 / 1000 },
  { wave: 7, dropRate: 1 / 750 },
  { wave: 8, dropRate: 1 / 500 },
  { wave: 9, dropRate: 1 / 250 },
];
function calculatePerKillRate(
  dropRateList: DropEntry[],
  currentWave: number
): number {
  // Find the drop rate for the current wave only
  const entry = dropRateList.find((e) => e.wave === currentWave);
  if (entry) {
    return entry.dropRate;
  }

  // For waves beyond 9, use the wave 9 drop rate
  if (currentWave > 9) {
    const lastEntry = dropRateList[dropRateList.length - 1];
    return lastEntry.dropRate;
  }

  return 0;
}

function calculateCumulativeChance(
  dropRateList: DropEntry[],
  currentWave: number,
  kills: number
): number {
  const p = calculatePerKillRate(dropRateList, currentWave);
  return 1 - Math.pow(1 - p, kills);
}

// Helpers to compute cumulative per-run stats across waves up to currentWave
function sumPerRunRates(
  dropRateList: DropEntry[],
  currentWave: number
): number {
  const cappedWave = Math.max(1, Math.floor(currentWave));
  const baseSum = dropRateList.reduce(
    (sum, e) => sum + (e.wave <= Math.min(cappedWave, 9) ? e.dropRate : 0),
    0
  );
  if (cappedWave > 9) {
    const extra = cappedWave - 9;
    const lastRate = dropRateList[dropRateList.length - 1].dropRate;
    return baseSum + extra * lastRate;
  }
  return baseSum;
}

function productPerRunNoDrop(
  dropRateList: DropEntry[],
  currentWave: number
): number {
  const cappedWave = Math.max(1, Math.floor(currentWave));
  let prod = 1;
  for (const e of dropRateList) {
    if (e.wave <= Math.min(cappedWave, 9)) {
      prod *= 1 - e.dropRate;
    }
  }
  if (cappedWave > 9) {
    const extra = cappedWave - 9;
    const lastRate = dropRateList[dropRateList.length - 1].dropRate;
    prod *= Math.pow(1 - lastRate, extra);
  }
  return prod;
}

export const WaveDropCalculator = ({
  currentWave,
  kills,
}: {
  currentWave: number;
  kills: number;
}) => {
  const [clothDropRate, setClothDropRate] = useState(0);
  const [eyeDropRate, setEyeDropRate] = useState(0);
  const [threadDropRate, setThreadDropRate] = useState(0);
  const [petDropRate, setPetDropRate] = useState(0);
  const [uniqueDropRate, setUniqueDropRate] = useState(0);

  // expected counts
  const [expCloth, setExpCloth] = useState(0);
  const [expEye, setExpEye] = useState(0);
  const [expThread, setExpThread] = useState(0);
  const [expPet, setExpPet] = useState(0);
  const [expUnique, setExpUnique] = useState(0);

  if (kills <= 0 || isNaN(kills)) kills = 1;
  if (currentWave <= 0 || isNaN(currentWave)) currentWave = 1;
  useEffect(() => {
    // Cumulative per-run expected rates (sum of per-wave probabilities)
    const expPerRunCloth = sumPerRunRates(mokhaiotlCloth, currentWave);
    const expPerRunEye = sumPerRunRates(eyeOfAyak, currentWave);
    const expPerRunThread = sumPerRunRates(avernicThreads, currentWave);
    const expPerRunPet = sumPerRunRates(petRates, currentWave);

    // Expected counts over all runs
    setExpCloth(kills * expPerRunCloth);
    setExpEye(kills * expPerRunEye);
    setExpThread(kills * expPerRunThread);
    setExpPet(kills * expPerRunPet);
    setExpUnique(kills * (expPerRunCloth + expPerRunEye + expPerRunThread));

    // Per-run no-drop products across all eligible waves
    const noDropClothPerRun = productPerRunNoDrop(mokhaiotlCloth, currentWave);
    const noDropEyePerRun = productPerRunNoDrop(eyeOfAyak, currentWave);
    const noDropThreadPerRun = productPerRunNoDrop(avernicThreads, currentWave);
    const noDropPetPerRun = productPerRunNoDrop(petRates, currentWave);

    // Probabilities across runs (at least one)
    setClothDropRate(1 - Math.pow(noDropClothPerRun, kills));
    setEyeDropRate(1 - Math.pow(noDropEyePerRun, kills));
    setThreadDropRate(1 - Math.pow(noDropThreadPerRun, kills));
    setPetDropRate(1 - Math.pow(noDropPetPerRun, kills));

    // Unique: per-run no-unique product across waves, then across runs
    const cappedWave = Math.max(1, Math.floor(currentWave));
    let noUniquePerRun = 1;
    // multiply per wave: (1 - p_cloth_w)(1 - p_eye_w)(1 - p_thread_w)
    for (let w = 1; w <= Math.min(cappedWave, 9); w++) {
      const clothW = mokhaiotlCloth.find((e) => e.wave === w)?.dropRate ?? 0;
      const eyeW = eyeOfAyak.find((e) => e.wave === w)?.dropRate ?? 0;
      const threadW = avernicThreads.find((e) => e.wave === w)?.dropRate ?? 0;
      noUniquePerRun *= (1 - clothW) * (1 - eyeW) * (1 - threadW);
    }
    if (cappedWave > 9) {
      const extra = cappedWave - 9;
      const lastCloth = mokhaiotlCloth[mokhaiotlCloth.length - 1].dropRate;
      const lastEye = eyeOfAyak[eyeOfAyak.length - 1].dropRate;
      const lastThread = avernicThreads[avernicThreads.length - 1].dropRate;
      const perExtraWaveNoUnique =
        (1 - lastCloth) * (1 - lastEye) * (1 - lastThread);
      noUniquePerRun *= Math.pow(perExtraWaveNoUnique, extra);
    }
    setUniqueDropRate(1 - Math.pow(noUniquePerRun, kills));
  }, [currentWave, kills]);

  return (
    <div className="results">
      <h2>
        Wave {currentWave} completed {kills} times
      </h2>

      <div className="reward">
        <img
          src="https://oldschool.runescape.wiki/images/thumb/Mokhaiotl_cloth_detail.png/130px-Mokhaiotl_cloth_detail.png?d06ed"
          alt=""
        />
        <p>
          Cloth probability:{" "}
          <strong>{Math.min(100, clothDropRate * 100).toFixed(2)}%</strong>
        </p>
        <p>
          Cloth expected: <strong>{expCloth.toFixed(3)}</strong>
        </p>
      </div>

      <div className="reward">
        <img
          src="https://oldschool.runescape.wiki/images/thumb/Eye_of_ayak_%28uncharged%29_detail.png/130px-Eye_of_ayak_%28uncharged%29_detail.png?0bf26"
          alt=""
        />
        <p>
          Eye probability:{" "}
          <strong>{Math.min(100, eyeDropRate * 100).toFixed(2)}%</strong>
        </p>
        <p>
          Eye expected: <strong>{expEye.toFixed(3)}</strong>
        </p>
      </div>

      <div className="reward">
        <img
          src="https://oldschool.runescape.wiki/images/thumb/Avernic_treads_detail.png/140px-Avernic_treads_detail.png?003ec"
          alt=""
        />

        <p>
          Threads probability:{" "}
          <strong>{Math.min(100, threadDropRate * 100).toFixed(2)}%</strong>
        </p>
        <p>
          Threads expected: <strong>{expThread.toFixed(3)}</strong>
        </p>
      </div>
      <div className="reward">
        <img
          src="https://oldschool.runescape.wiki/images/thumb/Monumental_chest_%28player%27s_unique%2C_closed%29.png/280px-Monumental_chest_%28player%27s_unique%2C_closed%29.png?02929"
          alt=""
        />
        <p>
          Unique probability:{" "}
          <strong>{Math.min(100, uniqueDropRate * 100).toFixed(2)}%</strong>
        </p>
        <p>
          Unique expected: <strong>{expUnique.toFixed(3)}</strong>
        </p>
      </div>
      <div className="reward">
        <img
          src="https://oldschool.runescape.wiki/images/Dom_%28follower%29.png?e7157"
          alt=""
        />
        <p>
          Pet probability:{" "}
          <strong> {Math.min(100, petDropRate * 100).toFixed(2)}%</strong>
        </p>
        <p>
          Pet expected: <strong>{expPet.toFixed(3)}</strong>
        </p>
      </div>
      <div className="note">
        <p>
          Note: The unique drop rate is calculated as the combined chance of
          getting at least one of the three unique drops (cloth, eye, threads).
          The expected value is the sum of individual expected values for each
          unique item.
        </p>
        <ul>
          <li>
            Expected per item = kills × sum of per-wave rates up to the selected
            wave
          </li>
          <li>
            Probability per item = 1 − (product over waves of (1 − rate))^kills
          </li>
          <li>
            Unique probability = 1 − (product over waves of (1 − cloth_w)(1 −
            eye_w)(1 − thread_w))^kills
          </li>
        </ul>
      </div>
    </div>
  );
};
